import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { MessageCircle, Heart, Shield, Clock, X } from 'lucide-react';
import { companyInfo, whatsappMessage } from '../data/mock';

// Conteúdo explicativo dos benefícios
const BENEFICIOS_INFO = {
  'Seguro e IPVA Incluído': {
    titulo: '🛡️ Seguro e IPVA Incluído',
    descricao: 'Você não precisa se preocupar com nada! Sua moto já vem com:',
    detalhes: [
      '✅ Seguro completo contra roubo e furto',
      '✅ Cobertura para acidentes',
      '✅ IPVA pago e em dia',
      '✅ Licenciamento regularizado',
      '✅ Sem custos extras ou surpresas',
      '✅ Total tranquilidade para trabalhar'
    ],
    footer: 'Tudo incluso no valor da semana! Você só precisa pilotar e trabalhar tranquilo.'
  },
  'Suporte Humanizado': {
    titulo: '💚 Suporte Humanizado',
    descricao: 'Atendimento de verdade, feito por pessoas que se importam:',
    detalhes: [
      '✅ Atendimento humanizado, não robôs',
      '✅ Resposta rápida via WhatsApp',
      '✅ Suporte durante toda a semana',
      '✅ Equipe que entende suas necessidades',
      '✅ Ajuda sempre que precisar',
      '✅ Relacionamento próximo e acolhedor'
    ],
    footer: 'Estamos aqui para você! Entre em contato pelo WhatsApp sempre que precisar.'
  },
  'Motos Zero KM': {
    titulo: '🏍️ Motos Zero KM',
    descricao: 'Trabalhe com o melhor! Todas as nossas motos são:',
    detalhes: [
      '✅ DK 160 zero quilômetro',
      '✅ Modelo mais econômico do mercado',
      '✅ Manutenção preventiva regular',
      '✅ Revisões em dia',
      '✅ Garantia de fábrica ativa',
      '✅ Máxima confiabilidade e economia'
    ],
    footer: 'Motos novas, econômicas e confiáveis para o seu trabalho render mais!'
  },
  'Atendimento Rápido': {
    titulo: '⚡ Atendimento Rápido',
    descricao: 'Seu tempo é valioso! Por isso oferecemos:',
    detalhes: [
      '✅ Aprovação rápida do cadastro',
      '✅ Documentação simplificada',
      '✅ Processo descomplicado',
      '✅ Retirada no mesmo dia (quando disponível)',
      '✅ Sem burocracia desnecessária',
      '✅ Agilidade do início ao fim'
    ],
    footer: 'Processo rápido para você começar a trabalhar o quanto antes!'
  }
};

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalAberto, setModalAberto] = useState(null);
  const [currentMotoIndex, setCurrentMotoIndex] = useState(0);
  const [isHoveringModal, setIsHoveringModal] = useState(false);
  const [closeTimer, setCloseTimer] = useState(null);
  const navigate = useNavigate();

  // Função para abrir modal (APENAS quando hover no card)
  const handleFeatureMouseEnter = (itemText) => {
    // Cancela qualquer timer de fechamento pendente
    if (closeTimer) {
      clearTimeout(closeTimer);
      setCloseTimer(null);
    }
    setModalAberto(itemText);
  };

  // Função quando mouse SAI do card
  const handleFeatureMouseLeave = () => {
    // Aguarda 300ms antes de fechar (tempo para mouse ir ao modal)
    const timer = setTimeout(() => {
      // Só fecha se não estiver no modal
      if (!isHoveringModal) {
        setModalAberto(null);
      }
    }, 300);
    setCloseTimer(timer);
  };

  // Função quando mouse ENTRA no modal
  const handleModalMouseEnter = () => {
    // Cancela o timer de fechamento
    if (closeTimer) {
      clearTimeout(closeTimer);
      setCloseTimer(null);
    }
    setIsHoveringModal(true);
  };

  // Função quando mouse SAI do modal
  const handleModalMouseLeave = () => {
    setIsHoveringModal(false);
    // Fecha o modal imediatamente quando sai dele
    setModalAberto(null);
  };

  // Imagens das motos com cores
  const motosImages = [
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/rla84wx1_IMG_2395.png",
      cor: "Vermelha",
      modelo: "DK 160"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/vhx69jw8_IMG_2396.png",
      cor: "Azul",
      modelo: "DK 160"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_moto-rental-app-4/artifacts/ch42em81_2025-05-19-13-36-09-Sem-Titulo-1.png",
      cor: "Preta",
      modelo: "DK 160"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Carousel automático de motos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMotoIndex((prev) => (prev + 1) % motosImages.length);
    }, 3000); // Troca a cada 3 segundos
    
    return () => clearInterval(interval);
  }, []);

  const openWhatsApp = () => {
    const message = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <>
      <style>{`
        @keyframes fadeInScale {
          0% { 
            opacity: 0; 
            transform: scale(0.95); 
          }
          100% { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
      `}</style>
      <section className="relative bg-gradient-to-br from-slate-900 via-green-900 to-emerald-900 py-20 lg:py-28 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
          alt="Background"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-green-900/90 to-emerald-900/95" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-green-500 rounded-full opacity-10 blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-emerald-500 rounded-full opacity-10 blur-3xl" />
        <div className="absolute bottom-20 right-1/3 w-64 h-64 bg-teal-500 rounded-full opacity-10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className={`text-center lg:text-left transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Heart className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium text-green-300">Os Melhores Preços da Baixada Santista</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Aluguel de Motos
              </span>
              <br />
              <span className="text-white">com o Melhor</span>
              <br />
              <span className="text-white">Custo-Benefício</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Motos <strong className="text-white">DK 160 zero km</strong> com preços que cabem no seu bolso. 
              Toda segurança e tranquilidade que você merece na sua <strong className="text-white">locação de moto</strong>.
            </p>
            
            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: Shield, text: 'Seguro e IPVA Incluído' },
                { icon: Clock, text: 'Suporte Humanizado' },
                { icon: Heart, text: 'Motos Zero KM' },
                { icon: MessageCircle, text: 'Atendimento Rápido' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="relative flex items-center gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm hover:bg-white/20 transition-all duration-300 hover:scale-105 cursor-pointer group"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onMouseEnter={() => handleFeatureMouseEnter(item.text)}
                  onMouseLeave={handleFeatureMouseLeave}
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium group-hover:underline decoration-2 underline-offset-4">{item.text}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                onClick={() => navigate('/reserva')}
                className="group relative bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 bg-[length:200%_auto] hover:bg-right text-white px-8 py-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 text-lg font-semibold overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Retirar Moto <span className="text-yellow-300 font-bold">AGORA!</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/planos')}
                className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-lg"
              >
                Ver Nossos Preços
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-white">{companyInfo.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="font-medium text-white">Clientes Satisfeitos</span>
              </div>
            </div>

            {/* Social Proof */}
            <div className="mt-6">
              <Button
                onClick={() => window.open('https://www.instagram.com/desloca_?igsh=MWppeTNuenlxd3hibA==', '_blank')}
                variant="ghost"
                className="group text-sm text-gray-300 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @desloca_ • Siga no Instagram
              </Button>
            </div>
          </div>
          
          {/* Right Side - Motorcycle Carousel */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl opacity-30 blur-2xl transition-opacity duration-500" />
              
              {/* Motorcycle Image with Carousel */}
              <div className="relative rounded-3xl shadow-2xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 min-h-[400px] flex items-center justify-center">
                <img 
                  key={currentMotoIndex}
                  src={motosImages[currentMotoIndex].image} 
                  alt={`${motosImages[currentMotoIndex].modelo} ${motosImages[currentMotoIndex].cor}`}
                  className="relative w-full h-full object-contain p-6 md:p-10 transition-all duration-700"
                  style={{
                    animation: 'fadeInScale 0.7s ease-in-out',
                    maxHeight: '450px'
                  }}
                />
                
                {/* Color Badge */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg z-10">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      motosImages[currentMotoIndex].cor === 'Vermelha' ? 'bg-red-500' :
                      motosImages[currentMotoIndex].cor === 'Azul' ? 'bg-blue-500' :
                      'bg-gray-900'
                    }`}></div>
                    <span className="text-sm font-semibold text-gray-800">{motosImages[currentMotoIndex].cor}</span>
                  </div>
                </div>

                {/* Dots Indicator */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                  {motosImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentMotoIndex(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        idx === currentMotoIndex 
                          ? 'bg-green-600 w-8' 
                          : 'bg-gray-400 w-2 hover:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                {/* Badge Zero KM */}
                <div className="absolute top-4 right-4 bg-gradient-to-br from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-xl shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300 z-10">
                  <div className="text-xs font-bold uppercase tracking-wide">Zero KM</div>
                </div>
              </div>
              
              {/* Floating Price Card - Moved below the image */}
              <div className="mt-6 bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 hover:scale-105 transition-transform duration-300">
                <div className="text-center">
                  <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">💰 Melhor Preço</div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">R$ 50,00</div>
                  <div className="text-sm text-gray-600 font-medium">por dia</div>
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500">Preço mais baixo da região</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Informações dos Benefícios */}
      {modalAberto && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => {
            setModalAberto(null);
            setIsHoveringModal(false);
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-slideUp"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={handleModalMouseEnter}
            onMouseLeave={handleModalMouseLeave}
          >
            {/* Header do Modal */}
            <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <h3 className="text-2xl font-bold pr-8">
                  {BENEFICIOS_INFO[modalAberto]?.titulo}
                </h3>
                <button
                  onClick={() => setModalAberto(null)}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-6">
              <p className="text-gray-700 font-medium mb-4">
                {BENEFICIOS_INFO[modalAberto]?.descricao}
              </p>

              <div className="space-y-3 mb-6">
                {BENEFICIOS_INFO[modalAberto]?.detalhes.map((detalhe, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100"
                  >
                    <span className="text-lg">{detalhe}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
                <p className="text-gray-800 text-sm font-medium text-center">
                  {BENEFICIOS_INFO[modalAberto]?.footer}
                </p>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setModalAberto(null);
                    navigate('/reserva');
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                >
                  Fazer Reserva
                </Button>
                <Button
                  onClick={() => setModalAberto(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
    </>
  );
};